import { useCallback, useEffect, useRef } from "react";
import { normalizePresetForResizeEditor, clearToolPrepSession, writeToolPrepSession, TOOL_PREP_KEYS, TOOL_PREP_ENTRY, isToolPrepKey, TOOL_PREP_PREVIEW_BG, OUTLINE_ENTRY_BACKGROUND, SHADOW_ENTRY_BACKGROUND, editorBackgroundFromToolPrepPreview } from "../../../utils/editorEntrySession";
import { parsePresetPixelSize } from "../../../utils/getTransform";
import ResizeToolPrep from "../../tools/layout/resize/Popup/ResizeToolPrep";
import ShadowToolPrep from "../../tools/object/shadow/Popup/ShadowToolPrep";
import OutlineToolPrep from "../../tools/object/outline/Popup/OutlineToolPrep";
import BackgroundColorToolPrep, { BACKGROUND_COLOR_ENTRY_DEFAULT } from "../../tools/background/backgroundColor/Popup/BackgroundColorToolPrep";
import { WhiteBackgroundToolPrep } from "../../tools/background/whiteBackground/Popup/WhiteBackgroundToolPrep";
import { BlackBackgroundToolPrep } from "../../tools/background/blackBackground/Popup/BlackBackgroundToolPrep";
import RotateToolPrep from "../../tools/object/rotate/Popup/RotateToolPrep";
import FlipToolPrep from "../../tools/object/flip/Popup/FlipToolPrep";
import PaddingToolPrep from "../../tools/layout/padding/Popup/PaddingToolPrep";
import CropToolPrep from "../../tools/layout/crop/Popup/CropToolPrep";
import CircleCropToolPrep from "../../tools/layout/circleCrop/Popup/CircleCropToolPrep";
import WatermarkToolPrep from "../../tools/watermark/Popup/WatermarkToolPrep";
import BlurToolPrep from "../../tools/background/blur/Popup/BlurToolPrep";
import PdfWatermarkerToolPrep from "../../tools/document/pdfWatermarker/Popup/PdfWatermarkerToolPrep";
import WordWatermarkerToolPrep from "../../tools/document/wordWatermarker/Popup/WordWatermarkerToolPrep";
import PdfPageNumbersToolPrep from "../../tools/document/pdfPageNumbers/Popup/PdfPageNumbersToolPrep";
import SignPdfToolPrep from "../../tools/document/signPdf/Popup/SignPdfToolPrep";
import OutpaintToolPrep from "../../tools/ai/outpaint/Popup/OutpaintToolPrep";
import RecolorToolPrep from "../../tools/ai/recolor/Popup/RecolorToolPrep";
import EnhanceToolPrep from "../../tools/ai/enhance/Popup/EnhanceToolPrep";
import BackgroundImageToolPrep from "../../tools/ai/backgroundImage/Popup/BackgroundImageToolPrep";
import AiShadowToolPrep from "../../tools/ai/shadow/Popup/AiShadowToolPrep";
import RemoveObjectToolPrep from "../../tools/ai/removeObject/Popup/RemoveObjectToolPrep";
import RemoveTextToolPrep from "../../tools/ai/removeText/Popup/RemoveTextToolPrep";
import UpscaleToolPrep from "../../tools/ai/upscale/Popup/UpscaleToolPrep";
import RelightToolPrep from "../../tools/ai/relight/Popup/RelightToolPrep";
import RemoveWatermarkToolPrep from "../../tools/ai/removeWatermark/Popup/RemoveWatermarkToolPrep";
import BackgroundRemoverToolPrep from "../../tools/ai/backgroundRemover/Popup/BackgroundRemoverToolPrep";
import ShadowRemoverToolPrep from "../../tools/ai/shadowRemover/Popup/ShadowRemoverToolPrep";
import OldPhotoRestoreToolPrep from "../../tools/ai/oldPhotoRestore/Popup/OldPhotoRestoreToolPrep";
import ColorizerToolPrep from "../../tools/ai/colorizer/Popup/ColorizerToolPrep";
import UnblurToolPrep from "../../tools/ai/unblur/Popup/UnblurToolPrep";
import ReplaceObjectToolPrep from "../../tools/ai/replaceObject/Popup/ReplaceObjectToolPrep";
import ReplaceTextToolPrep from "../../tools/ai/replaceText/Popup/ReplaceTextToolPrep";
import FaceSwapToolPrep from "../../tools/ai/faceSwap/Popup/FaceSwapToolPrep";
import { DEFAULT_BACKGROUND, useEditorStore, clearEditorSession } from "../../../state/editorStore";
import { fetchRemoteImageBlob } from "../../../utils/resolveCreationImageUrl";
import { adoptBlobUrl } from "../../../utils/adoptBlobUrl";
import {
  mergeToolPrepSubjectPlacement,
  subjectPlacementHandoffPlan,
} from "../../tools/lib/toolPrepSubjectPlacement";
import { toolPrepHandoffLocationState } from "./toolPrepHandoffState";

/**
 * Tool-prep overlay session: Done/Cancel handlers, deferred FIT/FILL and
 * padding apply, and the prep host. Editor only supplies document/session state.
 */
export function useEditorToolPrep(ctx) {
  const {
    appliedPaddingRef,
    appliedToolPrepTransformRef,
    applyManualEdit,
    background,
    bgRemovedImageUrl,
    canvasEditorRef,
    clearImage,
    creationId,
    inputPreview,
    location,
    navigate,
    originalImageUrl,
    originalImg,
    originalSubjectUrlRef,
    pendingPaddingApplyRef,
    pendingToolPrepApplyRef,
    processedImage,
    processedImageRef,
    removeBackgroundActive,
    rootCreationId,
    seedHistoryBaseline,
    selectedFile,
    setActiveMenuIndex,
    setAppliedResizePresetId,
    setBackground,
    setBackgroundApplied,
    setBackgroundControlPanelVisible,
    setBgCreationId,
    setBgRemovedImageUrl,
    setCanvasMeta,
    setEditorResizeMode,
    setExpandOutlineOnMount,
    setExpandShadowOnMount,
    setExpandWatermarkOnMount,
    setIsCanvasPreloading,
    setLastResizePreset,
    setLayersControlPanelVisible,
    setOriginalProcessedImage,
    setOutline,
    setProcessedImage,
    setRemoveBackgroundActive,
    setResizeControlPanelVisible,
    setShadow,
    setShadowSoftOverflow,
    setShowPreloader,
    setSubjectControlPanelVisible,
    setToolPrepEntryMode,
    setToolPrepKind,
    setUploadControlPanelVisible,
    setWatermark,
    storeCreationId,
    subjectReady,
    toolPrepEntryMode,
    toolPrepKind,
    transparentSourceId,
    updateObject,
  } = ctx;

  const canvasHasImage = useCallback(
    (url) => Boolean(url) && (url === processedImage || url === bgRemovedImageUrl),
    [bgRemovedImageUrl, processedImage]
  );

  const applyToolPrepTransform = useCallback((pending) => {
    if (!pending || !canvasEditorRef.current) return;
    const preset = normalizePresetForResizeEditor(pending.preset);
    const parsed = parsePresetPixelSize(preset);
    if (!parsed) return;

    setCanvasMeta({
      resolution: parsed.resolutionLabel,
      aspectRatio: parsed.aspectRatioLabel,
    });
    if (preset.id != null) setAppliedResizePresetId(preset.id);

    const mode = pending.resizeMode === "fill" ? "fill" : "fit";
    const applyWithNaturalSize = (iw, ih) => {
      const ok = canvasEditorRef.current?.applyPresetWithMode?.({
        aspectRatio: parsed.aspectRatio,
        mode,
        imageWidth: iw,
        imageHeight: ih,
      });
      if (ok) {
        appliedToolPrepTransformRef.current = true;
        pendingToolPrepApplyRef.current = null;
      }
    };

    const img = new Image();
    if (
      typeof pending.imageUrl === "string" &&
      /^https?:\/\//i.test(pending.imageUrl) &&
      !pending.imageUrl.startsWith("blob:") &&
      !pending.imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      applyWithNaturalSize(img.naturalWidth || img.width, img.naturalHeight || img.height);
    };
    img.onerror = () => {
      console.error("[applyToolPrepTransform] Failed to load image for transform");
    };
    img.src = pending.imageUrl;
  }, []);

  const applyPaddingLayout = useCallback((padding) => {
    if (!padding || !canvasEditorRef.current) return;
    const ok = canvasEditorRef.current?.applyPaddingLayout?.(padding);
    if (ok) {
      appliedPaddingRef.current = true;
      pendingPaddingApplyRef.current = null;
    }
  }, []);

  // Persist cutout creation so reload can restore via ?creationId= (blob URLs die).
  const commitToolPrepImageSession = useCallback(
    (imageUrl, creationIdFromPrep) => {
      const fromPrep =
        creationIdFromPrep != null && Number(creationIdFromPrep) > 0
          ? Number(creationIdFromPrep)
          : null;
      const fromStore =
        storeCreationId != null && Number(storeCreationId) > 0
          ? Number(storeCreationId)
          : null;
      const fromHook =
        creationId != null && Number(creationId) > 0
          ? Number(creationId)
          : null;
      const cid = fromPrep || fromStore || fromHook;
      if (!cid || !imageUrl) return;
      setBgCreationId(cid);
      useEditorStore.getState().commitImageSession(cid, imageUrl, {
        transparentSourceId: cid,
        rootCreationId: cid,
      });
      if (window.location.search !== `?creationId=${cid}`) {
        navigate(`/editor?creationId=${cid}`, {
          replace: true,
          state: toolPrepHandoffLocationState(location?.state),
        });
      }
    },
    [creationId, location, navigate, setBgCreationId, storeCreationId]
  );

  const handleToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      let imageUrl = result.imageUrl;
      // Safety: never put raw HTTPS onto canvas (preview <img> can show S3; canvas cannot)
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
          );
          return;
        }
      }

      if (result.effectBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error("[handleToolPrepDone] adopt image blob failed", err);
          alert("Could not keep the resized image for the editor. Please try again.");
          return;
        }

        if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || processedImage || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);

        if (result.removeBg) {
          setBgRemovedImageUrl(imageUrl);
          setRemoveBackgroundActive(true);
          commitToolPrepImageSession(imageUrl, result.creationId);
        } else {
          setRemoveBackgroundActive(false);
        }

        applyManualEdit({
          imageUrl,
          creationId: result.removeBg ? result.creationId : undefined,
          background: { ...DEFAULT_BACKGROUND },
        });
        setProcessedImage(imageUrl);
        setBackground({ ...DEFAULT_BACKGROUND });
        setBackgroundApplied(false);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      if (!result?.preset) return;

      if (canvasHasImage(imageUrl)) {
        clearToolPrepSession();
        setToolPrepKind(null);
        setLastResizePreset(result.preset);
        setEditorResizeMode(result.resizeMode === "fill" ? "fill" : "fit");
        if (result.preset?.id != null) {
          setAppliedResizePresetId(result.preset.id);
        }
        const pending = {
          preset: result.preset,
          resizeMode: result.resizeMode === "fill" ? "fill" : "fit",
          imageUrl,
        };
        pendingToolPrepApplyRef.current = pending;
        appliedToolPrepTransformRef.current = false;
        canvasEditorRef.current?.setLayoutLock?.(true);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        const tryApply = () => {
          if (!pendingToolPrepApplyRef.current || appliedToolPrepTransformRef.current) return;
          applyToolPrepTransform(pendingToolPrepApplyRef.current);
        };
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(tryApply);
          window.setTimeout(tryApply, 120);
          window.setTimeout(tryApply, 400);
        });
        return;
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);
      } else {
        setRemoveBackgroundActive(false);
      }

      setLastResizePreset(result.preset);
      setEditorResizeMode(result.resizeMode === "fill" ? "fill" : "fit");
      if (result.preset?.id != null) {
        setAppliedResizePresetId(result.preset.id);
      }

      const pending = {
        preset: result.preset,
        resizeMode: result.resizeMode === "fill" ? "fill" : "fit",
        imageUrl,
      };
      pendingToolPrepApplyRef.current = pending;
      appliedToolPrepTransformRef.current = false;

      // Prevent CanvasEditor auto-contain layout from overwriting FILL/FIT
      canvasEditorRef.current?.setLayoutLock?.(true);

      applyManualEdit({
        imageUrl,
        creationId: result.removeBg ? result.creationId : undefined,
        background: { ...DEFAULT_BACKGROUND },
      });
      setProcessedImage(imageUrl);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);

      // Apply immediately (same URL may not re-trigger subjectReady)
      const tryApply = () => {
        if (!pendingToolPrepApplyRef.current || appliedToolPrepTransformRef.current) return;
        applyToolPrepTransform(pendingToolPrepApplyRef.current);
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(tryApply);
        window.setTimeout(tryApply, 120);
        window.setTimeout(tryApply, 400);
      });
    },
    [
      applyManualEdit,
      applyToolPrepTransform,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      setProcessedImage,
    ]
  );

  const handleToolPrepCancel = useCallback(() => {
    const fromEditor = toolPrepEntryMode === TOOL_PREP_ENTRY.EDITOR;
    clearToolPrepSession();
    setToolPrepKind(null);
    setToolPrepEntryMode(null);
    setExpandShadowOnMount(false);
    setExpandOutlineOnMount(false);
    if (fromEditor) {
      return;
    }
    setRemoveBackgroundActive(false);
    try {
      clearImage?.();
    } catch {
      // ignore
    }
    try {
      clearEditorSession?.();
    } catch {
      // ignore
    }
    navigate("/dashboard");
  }, [clearImage, navigate, toolPrepEntryMode]);

  const openToolPrepFromEditor = useCallback((kind) => {
    if (!isToolPrepKey(kind)) return;
    writeToolPrepSession({
      toolPrep: kind,
      entryMode: TOOL_PREP_ENTRY.EDITOR,
    });
    setToolPrepEntryMode(TOOL_PREP_ENTRY.EDITOR);
    setToolPrepKind(kind);
  }, []);

  const handleShadowToolPrepCancel = handleToolPrepCancel;
  const handleOutlineToolPrepCancel = handleToolPrepCancel;
  const handleBackgroundColorToolPrepCancel = handleToolPrepCancel;
  const handleTransformToolPrepCancel = handleToolPrepCancel;
  const handleWatermarkToolPrepCancel = handleToolPrepCancel;
  const handlePdfWatermarkerToolPrepCancel = handleToolPrepCancel;
  const handleWordWatermarkerToolPrepCancel = handleToolPrepCancel;
  const handleDocumentExportStayOpen = useCallback((result) => {
    if (result?.exported || result?.stayOpen) return;
    handleToolPrepCancel();
  }, [handleToolPrepCancel]);
  const handlePdfPageNumbersToolPrepCancel = handleToolPrepCancel;
  const handlePdfPageNumbersToolPrepDone = handleDocumentExportStayOpen;
  const handleOutpaintToolPrepCancel = handleToolPrepCancel;
  const handleRecolorToolPrepCancel = handleToolPrepCancel;
  const handleEnhanceToolPrepCancel = handleToolPrepCancel;
  const handleAiFeatureToolPrepCancel = handleToolPrepCancel;

  const handleShadowToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      const alreadyOnCanvas =
        !result.effectBaked &&
        (result.imageUrl === processedImage ||
          result.imageUrl === bgRemovedImageUrl);
      if (alreadyOnCanvas) {
        clearToolPrepSession();
        setToolPrepKind(null);
        if (result.shadow) {
          setShadow({ ...result.shadow }, { recordHistory: false });
        }
        if (result.preserveShadowLayout) {
          setShadowSoftOverflow(true);
        } else {
          setShadowSoftOverflow(false);
        }
        const nextBg =
          editorBackgroundFromToolPrepPreview(
            result,
            SHADOW_ENTRY_BACKGROUND
          ) || { ...DEFAULT_BACKGROUND };
        setBackground(nextBg);
        setBackgroundApplied(nextBg.type === "color");
        setExpandShadowOnMount(true);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleShadowToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      if (result.effectBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error("[handleShadowToolPrepDone] adopt image blob failed", err);
          alert("Could not keep the image for the editor. Please try again.");
          return;
        }

        // Prefer tool upload original — never treat cutout/processed as original.
        if (result.originalImageUrl) {
          try {
            originalSubjectUrlRef.current = await adoptBlobUrl(
              result.originalImageUrl
            );
          } catch {
            originalSubjectUrlRef.current = result.originalImageUrl;
          }
        } else if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);

        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);

        // Match tool-prep stage (e.g. white Preview card), not transparent by default.
        const bakedBg =
          editorBackgroundFromToolPrepPreview(
            result,
            SHADOW_ENTRY_BACKGROUND
          ) || { ...DEFAULT_BACKGROUND };

        applyManualEdit({
          imageUrl,
          creationId: result.creationId,
          background: bakedBg,
        });
        setBackground(bakedBg);
        setBackgroundApplied(bakedBg.type === "color");
        setProcessedImage(imageUrl);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      if (result.originalImageUrl) {
        try {
          originalSubjectUrlRef.current = await adoptBlobUrl(
            result.originalImageUrl
          );
        } catch {
          originalSubjectUrlRef.current = result.originalImageUrl;
        }
      } else if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      setBgRemovedImageUrl(imageUrl);
      setRemoveBackgroundActive(true);
      commitToolPrepImageSession(imageUrl, result.creationId);

      if (result.shadow) {
        setShadow({ ...result.shadow }, { recordHistory: false });
      }
      // AI baked soft-shadow may preserve layout; object shadow must tighten BB.
      if (result.preserveShadowLayout) {
        setShadowSoftOverflow(true);
      } else {
        setShadowSoftOverflow(false);
      }

      // Match tool-prep stage preview on the editor canvas.
      const nextBg =
        editorBackgroundFromToolPrepPreview(
          result,
          SHADOW_ENTRY_BACKGROUND
        ) || { ...DEFAULT_BACKGROUND };

      applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: nextBg,
      });
      setBackground(nextBg);
      setBackgroundApplied(nextBg.type === "color");
      setProcessedImage(imageUrl);
      setExpandShadowOnMount(true);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      bgRemovedImageUrl,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      setBackground,
      setProcessedImage,
      setShadow,
      setShadowSoftOverflow,
    ]
  );

  const handleOutlineToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      if (!result.effectBaked && canvasHasImage(result.imageUrl)) {
        clearToolPrepSession();
        setToolPrepKind(null);
        setIsCanvasPreloading(false);
        setShowPreloader(false);
        if (result.outline) {
          setOutline({ ...result.outline }, { recordHistory: false });
        }
        const nextBg =
          editorBackgroundFromToolPrepPreview(
            result,
            OUTLINE_ENTRY_BACKGROUND
          ) || { ...DEFAULT_BACKGROUND };
        setBackground(nextBg);
        setBackgroundApplied(nextBg.type === "color");
        setExpandOutlineOnMount(true);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleOutlineToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      if (result.effectBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error("[handleOutlineToolPrepDone] adopt image blob failed", err);
          alert("Could not keep the image for the editor. Please try again.");
          return;
        }

        if (result.originalImageUrl) {
          try {
            originalSubjectUrlRef.current = await adoptBlobUrl(
              result.originalImageUrl
            );
          } catch {
            originalSubjectUrlRef.current = result.originalImageUrl;
          }
        } else if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || processedImage || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);

        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        setIsCanvasPreloading(false);
        setShowPreloader(false);
        commitToolPrepImageSession(imageUrl, result.creationId);

        if (!useEditorStore.getState()._historySeeded) {
          seedHistoryBaseline(imageUrl);
        }

        const bakedBg =
          editorBackgroundFromToolPrepPreview(
            result,
            OUTLINE_ENTRY_BACKGROUND
          ) || { ...DEFAULT_BACKGROUND };

        const applied = applyManualEdit({
          imageUrl,
          creationId: result.creationId,
          background: bakedBg,
        });
        if (!applied) {
          useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
          setBackground(bakedBg, { recordHistory: false });
        }

        setProcessedImage(imageUrl);
        setBackground(bakedBg);
        setBackgroundApplied(bakedBg.type === "color");
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      if (result.originalImageUrl) {
        try {
          originalSubjectUrlRef.current = await adoptBlobUrl(
            result.originalImageUrl
          );
        } catch {
          originalSubjectUrlRef.current = result.originalImageUrl;
        }
      } else if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      setBgRemovedImageUrl(imageUrl);
      setRemoveBackgroundActive(true);
      setIsCanvasPreloading(false);
      setShowPreloader(false);
      commitToolPrepImageSession(imageUrl, result.creationId);

      if (result.outline) {
        setOutline({ ...result.outline }, { recordHistory: false });
      }

      const nextBg =
        editorBackgroundFromToolPrepPreview(
          result,
          OUTLINE_ENTRY_BACKGROUND
        ) || { ...DEFAULT_BACKGROUND };

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const applied = applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: nextBg,
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
        setBackground(nextBg, { recordHistory: false });
      } else {
        setBackground(nextBg);
      }

      setProcessedImage(imageUrl);
      setBackgroundApplied(nextBg.type === "color");
      setExpandOutlineOnMount(true);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      seedHistoryBaseline,
      setBackground,
      setOutline,
      setProcessedImage,
    ]
  );

  const handleBackgroundColorToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      if (!result.effectBaked && canvasHasImage(result.imageUrl)) {
        clearToolPrepSession();
        setToolPrepKind(null);
        setIsCanvasPreloading(false);
        setShowPreloader(false);
        const colorBg =
          editorBackgroundFromToolPrepPreview(
            result,
            BACKGROUND_COLOR_ENTRY_DEFAULT
          ) || { ...DEFAULT_BACKGROUND };
        setBackground(colorBg);
        setBackgroundApplied(colorBg.type === "color");
        setActiveMenuIndex(1);
        setSubjectControlPanelVisible(false);
        setBackgroundControlPanelVisible(true);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleBackgroundColorToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      if (result.effectBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error(
            "[handleBackgroundColorToolPrepDone] adopt image blob failed",
            err
          );
          alert("Could not keep the image for the editor. Please try again.");
          return;
        }

        if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || processedImage || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);

        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        setIsCanvasPreloading(false);
        setShowPreloader(false);
        commitToolPrepImageSession(imageUrl, result.creationId);

        if (!useEditorStore.getState()._historySeeded) {
          seedHistoryBaseline(imageUrl);
        }

        const bakedBg =
          editorBackgroundFromToolPrepPreview(
            result,
            BACKGROUND_COLOR_ENTRY_DEFAULT
          ) || { ...DEFAULT_BACKGROUND };

        const applied = applyManualEdit({
          imageUrl,
          creationId: result.creationId,
          background: bakedBg,
        });
        if (!applied) {
          useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
          setBackground(bakedBg, { recordHistory: false });
        }

        setProcessedImage(imageUrl);
        setBackground(bakedBg);
        setBackgroundApplied(bakedBg.type === "color");
        setActiveMenuIndex(bakedBg.type === "color" ? 1 : 0);
        setSubjectControlPanelVisible(bakedBg.type !== "color");
        setBackgroundControlPanelVisible(bakedBg.type === "color");
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      setBgRemovedImageUrl(imageUrl);
      setRemoveBackgroundActive(true);
      setIsCanvasPreloading(false);
      setShowPreloader(false);
      commitToolPrepImageSession(imageUrl, result.creationId);

      const colorBg =
        editorBackgroundFromToolPrepPreview(
          result,
          BACKGROUND_COLOR_ENTRY_DEFAULT
        ) || { ...DEFAULT_BACKGROUND };

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const applied = applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: colorBg,
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
        setBackground(colorBg, { recordHistory: false });
      }

      setProcessedImage(imageUrl);
      setBackgroundApplied(colorBg.type === "color");
      setActiveMenuIndex(1);
      setSubjectControlPanelVisible(false);
      setBackgroundControlPanelVisible(true);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      seedHistoryBaseline,
      setBackground,
      setProcessedImage,
    ]
  );

  const handleTransformToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      if (canvasHasImage(result.imageUrl)) {
        clearToolPrepSession();
        setToolPrepKind(null);
        updateObject("rotation", Number(result.rotation) || 0, {
          recordHistory: false,
        });
        updateObject("flipH", Boolean(result.flipH), { recordHistory: false });
        updateObject("flipV", Boolean(result.flipV), { recordHistory: false });
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleTransformToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
          );
          return;
        }
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);
      } else {
        setRemoveBackgroundActive(false);
      }

      const nextBg =
        result.removeBg && result.previewBg === TOOL_PREP_PREVIEW_BG.WHITE
          ? {
              type: "color",
              color: "#FFFFFF",
              imageUrl: null,
              aiConfig: null,
            }
          : { ...DEFAULT_BACKGROUND };

      applyManualEdit({
        imageUrl,
        creationId: result.removeBg ? result.creationId : undefined,
        background: nextBg,
      });
      setBackground(nextBg);
      setBackgroundApplied(nextBg.type === "color");
      setProcessedImage(imageUrl);

      updateObject("rotation", Number(result.rotation) || 0, {
        recordHistory: false,
      });
      updateObject("flipH", Boolean(result.flipH), { recordHistory: false });
      updateObject("flipV", Boolean(result.flipV), { recordHistory: false });

      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      setBackground,
      setProcessedImage,
      updateObject,
    ]
  );

  const handlePaddingToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      if (!result.paddingBaked && canvasHasImage(result.imageUrl)) {
        clearToolPrepSession();
        setToolPrepKind(null);
        const padding = result.padding || {};
        pendingPaddingApplyRef.current = padding;
        appliedPaddingRef.current = false;
        canvasEditorRef.current?.setLayoutLock?.(true);
        const paddingFill =
          padding.fillColor && padding.fillColor !== "transparent"
            ? padding.fillColor
            : null;
        if (paddingFill) {
          const paddingBackground = {
            type: "color",
            color: paddingFill,
            imageUrl: null,
          };
          setBackground(paddingBackground);
          setBackgroundApplied(true);
        }
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        const tryApply = () => {
          if (!pendingPaddingApplyRef.current || appliedPaddingRef.current) return;
          applyPaddingLayout(pendingPaddingApplyRef.current);
        };
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(tryApply);
          window.setTimeout(tryApply, 120);
          window.setTimeout(tryApply, 400);
        });
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handlePaddingToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
          );
          return;
        }
      }

      if (result.paddingBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error("[handlePaddingToolPrepDone] adopt image blob failed", err);
          alert("Could not keep the padded image for the editor. Please try again.");
          return;
        }

        if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || processedImage || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);

        if (result.removeBg) {
          setBgRemovedImageUrl(imageUrl);
          setRemoveBackgroundActive(true);
          commitToolPrepImageSession(imageUrl, result.creationId);
        } else {
          setRemoveBackgroundActive(false);
        }

        applyManualEdit({
          imageUrl,
          creationId: result.removeBg ? result.creationId : undefined,
          background: { ...DEFAULT_BACKGROUND },
        });
        setProcessedImage(imageUrl);
        setBackground({ ...DEFAULT_BACKGROUND });
        setBackgroundApplied(false);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      const padding = result.padding || {};
      pendingPaddingApplyRef.current = padding;
      appliedPaddingRef.current = false;
      canvasEditorRef.current?.setLayoutLock?.(true);

      const paddingFill =
        padding.fillColor && padding.fillColor !== "transparent"
          ? padding.fillColor
          : null;
      const paddingBackground = paddingFill
        ? { type: "color", color: paddingFill, imageUrl: null }
        : { ...DEFAULT_BACKGROUND };

      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);
      } else {
        setRemoveBackgroundActive(false);
      }

      applyManualEdit({
        imageUrl,
        creationId: result.removeBg ? result.creationId : undefined,
        background: paddingBackground,
      });
      setProcessedImage(imageUrl);
      setBackground(paddingBackground);
      setBackgroundApplied(Boolean(paddingFill));
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);

      const tryApply = () => {
        if (!pendingPaddingApplyRef.current || appliedPaddingRef.current) return;
        applyPaddingLayout(pendingPaddingApplyRef.current);
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(tryApply);
        window.setTimeout(tryApply, 120);
        window.setTimeout(tryApply, 400);
      });
    },
    [
      applyManualEdit,
      applyPaddingLayout,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      setBackground,
      setProcessedImage,
    ]
  );

  const handleCropToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleCropToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
          );
          return;
        }
      }

      try {
        imageUrl = await adoptBlobUrl(imageUrl);
      } catch (err) {
        console.error("[handleCropToolPrepDone] adopt image blob failed", err);
        alert("Could not keep the cropped image for the editor. Please try again.");
        return;
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);
      } else {
        setRemoveBackgroundActive(false);
      }

      applyManualEdit({
        imageUrl,
        creationId: result.removeBg ? result.creationId : undefined,
        background: { ...DEFAULT_BACKGROUND },
      });
      setProcessedImage(imageUrl);
      setBackground({ ...DEFAULT_BACKGROUND });
      setBackgroundApplied(false);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      setBackground,
      setProcessedImage,
    ]
  );

  const handleWatermarkToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      if (!result.effectBaked && canvasHasImage(result.imageUrl)) {
        let watermarkPayload = result.watermark ? { ...result.watermark } : null;
        if (watermarkPayload?.logoUrl) {
          try {
            watermarkPayload = {
              ...watermarkPayload,
              logoUrl: await adoptBlobUrl(watermarkPayload.logoUrl),
            };
          } catch (err) {
            console.error("[handleWatermarkToolPrepDone] adopt logo blob failed", err);
            watermarkPayload = {
              ...watermarkPayload,
              logoUrl: null,
              logoNaturalWidth: 0,
              logoNaturalHeight: 0,
            };
          }
        }
        clearToolPrepSession();
        setToolPrepKind(null);
        setIsCanvasPreloading(false);
        setShowPreloader(false);
        if (watermarkPayload) {
          setWatermark(
            { enabled: true, ...watermarkPayload },
            { recordHistory: false }
          );
        }
        setExpandWatermarkOnMount(true);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleWatermarkToolPrepDone] CORS blobify failed", err);
          alert(
            "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
          );
          return;
        }
      }

      if (result.effectBaked) {
        try {
          imageUrl = await adoptBlobUrl(imageUrl);
        } catch (err) {
          console.error("[handleWatermarkToolPrepDone] adopt image blob failed", err);
          alert("Could not keep the image for the editor. Please try again.");
          return;
        }

        if (!originalSubjectUrlRef.current) {
          originalSubjectUrlRef.current =
            originalImageUrl || inputPreview || processedImage || null;
        }

        clearToolPrepSession();
        setToolPrepKind(null);
        setIsCanvasPreloading(false);
        setShowPreloader(false);

        if (result.removeBg) {
          setBgRemovedImageUrl(imageUrl);
          setRemoveBackgroundActive(true);
          commitToolPrepImageSession(imageUrl, result.creationId);
        } else {
          setRemoveBackgroundActive(false);
        }

        if (!useEditorStore.getState()._historySeeded) {
          seedHistoryBaseline(imageUrl);
        }

        const bakedBg =
          editorBackgroundFromToolPrepPreview(result, "#FFFFFF") || {
            ...DEFAULT_BACKGROUND,
          };

        const applied = applyManualEdit({
          imageUrl,
          creationId: result.removeBg ? result.creationId : undefined,
          background: bakedBg,
        });
        if (!applied) {
          useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
          setBackground(bakedBg, { recordHistory: false });
        } else {
          setBackground(bakedBg);
        }
        setBackgroundApplied(bakedBg.type === "color");
        setProcessedImage(imageUrl);
        setActiveMenuIndex(0);
        setSubjectControlPanelVisible(true);
        setBackgroundControlPanelVisible(false);
        setResizeControlPanelVisible(false);
        setLayersControlPanelVisible(false);
        setUploadControlPanelVisible(false);
        return;
      }

      // Prep popup revokes its blob URLs on unmount — clone before closing it.
      try {
        imageUrl = await adoptBlobUrl(imageUrl);
      } catch (err) {
        console.error("[handleWatermarkToolPrepDone] adopt image blob failed", err);
        alert("Could not keep the image for the editor. Please try again.");
        return;
      }

      let watermarkPayload = result.watermark ? { ...result.watermark } : null;
      if (watermarkPayload?.logoUrl) {
        try {
          watermarkPayload = {
            ...watermarkPayload,
            logoUrl: await adoptBlobUrl(watermarkPayload.logoUrl),
          };
        } catch (err) {
          console.error("[handleWatermarkToolPrepDone] adopt logo blob failed", err);
          watermarkPayload = {
            ...watermarkPayload,
            logoUrl: null,
            logoNaturalWidth: 0,
            logoNaturalHeight: 0,
          };
        }
      }

      if (!originalSubjectUrlRef.current) {
        originalSubjectUrlRef.current =
          originalImageUrl || inputPreview || processedImage || null;
      }

      clearToolPrepSession();
      setToolPrepKind(null);
      setIsCanvasPreloading(false);
      setShowPreloader(false);

      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
        commitToolPrepImageSession(imageUrl, result.creationId);
      } else {
        setRemoveBackgroundActive(false);
      }

      const nextBg =
        result.removeBg && result.previewBg === TOOL_PREP_PREVIEW_BG.WHITE
          ? {
              type: "color",
              color: "#FFFFFF",
              imageUrl: null,
              aiConfig: null,
            }
          : { ...DEFAULT_BACKGROUND };

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const applied = applyManualEdit({
        imageUrl,
        creationId: result.removeBg ? result.creationId : undefined,
        background: nextBg,
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
        setBackground(nextBg, { recordHistory: false });
      } else {
        setBackground(nextBg);
      }
      setBackgroundApplied(nextBg.type === "color");
      setProcessedImage(imageUrl);

      if (watermarkPayload) {
        setWatermark(
          { enabled: true, ...watermarkPayload },
          { recordHistory: false }
        );
      }

      setExpandWatermarkOnMount(true);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      canvasHasImage,
      commitToolPrepImageSession,
      inputPreview,
      originalImageUrl,
      processedImage,
      seedHistoryBaseline,
      setBackground,
      setProcessedImage,
      setWatermark,
    ]
  );

  const handleOutpaintToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) return;

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleOutpaintToolPrepDone] CORS blobify failed", err);
          alert(
            "Expanded image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      clearToolPrepSession();
      setToolPrepKind(null);

      commitToolPrepImageSession(imageUrl, result.creationId);

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const applied = applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: { ...DEFAULT_BACKGROUND },
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
      }

      setProcessedImage(imageUrl);
      setOriginalProcessedImage(imageUrl);
      setRemoveBackgroundActive(false);

      const w = Number(result.width) || 0;
      const h = Number(result.height) || 0;
      if (w > 0 && h > 0) {
        const gcd = (a, b) => {
          let x = Math.round(Math.abs(a));
          let y = Math.round(Math.abs(b));
          while (y) {
            const t = y;
            y = x % y;
            x = t;
          }
          return x || 1;
        };
        const g = gcd(w, h);
        const arLabel = `${Math.round(w / g)}:${Math.round(h / g)}`;
        pendingToolPrepApplyRef.current = {
          preset: {
            id: `outpaint-${w}x${h}`,
            dimensions: `${w} × ${h}, ${arLabel}`,
            label: `${w} × ${h}, ${arLabel}`,
          },
          resizeMode: "fill",
          imageUrl,
        };
        appliedToolPrepTransformRef.current = false;
      }

      const tryApply = () => {
        if (!pendingToolPrepApplyRef.current || appliedToolPrepTransformRef.current) return;
        applyToolPrepTransform(pendingToolPrepApplyRef.current);
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(tryApply);
        window.setTimeout(tryApply, 120);
        window.setTimeout(tryApply, 400);
      });

      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      commitToolPrepImageSession,
      seedHistoryBaseline,
      setOriginalProcessedImage,
      setProcessedImage,
    ]
  );

  const handleRecolorToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) {
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleRecolorToolPrepDone] CORS blobify failed", err);
          alert(
            "Recolored image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      clearToolPrepSession();
      setToolPrepKind(null);
      commitToolPrepImageSession(imageUrl, result.creationId);

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const applied = applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: { ...DEFAULT_BACKGROUND },
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
      }

      setProcessedImage(imageUrl);
      setOriginalProcessedImage(imageUrl);
      setRemoveBackgroundActive(false);
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      applyToolPrepTransform,
      commitToolPrepImageSession,
      seedHistoryBaseline,
      setOriginalProcessedImage,
      setProcessedImage,
    ]
  );

  const pendingSelectObjectRef = useRef(false);

  const handleEnhanceToolPrepDone = useCallback(
    async (result) => {
      if (!result?.imageUrl) {
        return;
      }

      let imageUrl = result.imageUrl;
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        try {
          imageUrl = await fetchRemoteImageBlob(imageUrl);
        } catch (err) {
          console.error("[handleEnhanceToolPrepDone] CORS blobify failed", err);
          alert(
            "Enhanced image could not be loaded for the editor (CORS). Please try again."
          );
          return;
        }
      }

      clearToolPrepSession();
      setToolPrepKind(null);
      commitToolPrepImageSession(imageUrl, result.creationId);

      if (result.preserveShadowLayout) {
        setShadowSoftOverflow(true);
        const canvas = canvasEditorRef.current;
        const size = canvas?.getCanvasSize?.();
        const currentShown = processedImageRef.current;
        const alreadyThisResult =
          currentShown === result.imageUrl || currentShown === imageUrl;
        if (size?.width > 0 && currentShown && !alreadyThisResult) {
          canvas.setPreserveObjectLayoutOnce?.();
        }
      } else {
        setShadowSoftOverflow(false);
      }

      if (!useEditorStore.getState()._historySeeded) {
        seedHistoryBaseline(imageUrl);
      }

      const nextBackground = result.background?.type
        ? { ...DEFAULT_BACKGROUND, ...result.background }
        : { ...DEFAULT_BACKGROUND };
      const applied = applyManualEdit({
        imageUrl,
        creationId: result.creationId,
        background: nextBackground,
      });
      if (!applied) {
        useEditorStore.getState().setImageUrl(imageUrl, { recordHistory: false });
        if (result.background?.type) {
          setBackground(nextBackground, { recordHistory: false });
        }
      }

      if (result.selectSubject) {
        pendingSelectObjectRef.current = true;
      }

      setProcessedImage(imageUrl);
      setOriginalProcessedImage(imageUrl);
      if (result.removeBg) {
        setBgRemovedImageUrl(imageUrl);
        setRemoveBackgroundActive(true);
      } else {
        setRemoveBackgroundActive(false);
      }
      setActiveMenuIndex(0);
      setSubjectControlPanelVisible(true);
      setBackgroundControlPanelVisible(false);
      setResizeControlPanelVisible(false);
      setLayersControlPanelVisible(false);
      setUploadControlPanelVisible(false);
    },
    [
      applyManualEdit,
      commitToolPrepImageSession,
      seedHistoryBaseline,
      setBackground,
      setOriginalProcessedImage,
      setProcessedImage,
    ]
  );

  /** Shared Done handler for AI Background / Shadow / Remove* / Upscale / Relight prep. */
  const handleAiFeatureToolPrepDone = handleEnhanceToolPrepDone;

  /**
   * Every Open in Editor path. Copies a user-resized subject box onto the
   * result, then asks the canvas to keep that frame instead of re-fitting
   * the original cutout.
   */
  const deliverToolPrepResult = useCallback(
    async (handler, result) => {
      const next = mergeToolPrepSubjectPlacement(result);
      const plan = subjectPlacementHandoffPlan(next);
      const prime = () => {
        const canvas = canvasEditorRef.current;
        if (!canvas?.setPendingSubjectPlacement) return false;
        if (!plan) {
          canvas.setPendingSubjectPlacement(null);
          return true;
        }
        if (plan.applyRotation) {
          updateObject("rotation", plan.rotation || 0, {
            recordHistory: false,
          });
        }
        canvas.setPendingSubjectPlacement(plan);
        return true;
      };
      const flush = () => {
        canvasEditorRef.current?.flushPendingSubjectPlacement?.(next?.imageUrl);
      };
      if (!prime()) {
        requestAnimationFrame(() => {
          prime();
          flush();
        });
      }
      try {
        return await handler(next);
      } finally {
        flush();
      }
    },
    [updateObject]
  );

  const inboundApplyOnceRef = useRef(false);
  useEffect(() => {
    if (inboundApplyOnceRef.current) return;
    const pending = location?.state?.toolPrepApply;
    if (!pending?.kind || !pending?.result) return;
    inboundApplyOnceRef.current = true;

    const { kind, result } = pending;
    navigate(`${location.pathname}${location.search || ""}`, {
      replace: true,
      state: {
        ...(location.state || {}),
        toolPrepApply: null,
        deferBackgroundRemoval: true,
        processedImage:
          result?.imageUrl || location.state?.processedImage || null,
      },
    });

    switch (kind) {
      case TOOL_PREP_KEYS.RESIZE:
        void deliverToolPrepResult(handleToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.SHADOW:
        void deliverToolPrepResult(handleShadowToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.OUTLINE:
        void deliverToolPrepResult(handleOutlineToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.BACKGROUND_COLOR:
      case TOOL_PREP_KEYS.WHITE_BACKGROUND:
      case TOOL_PREP_KEYS.BLACK_BACKGROUND:
        void deliverToolPrepResult(handleBackgroundColorToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.ROTATE:
      case TOOL_PREP_KEYS.FLIP:
        void deliverToolPrepResult(handleTransformToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.PADDING:
        void deliverToolPrepResult(handlePaddingToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.CROP:
      case TOOL_PREP_KEYS.CIRCLE_CROP:
        void deliverToolPrepResult(handleCropToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.WATERMARK_MAKER:
        void deliverToolPrepResult(handleWatermarkToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.BLUR:
      case TOOL_PREP_KEYS.GAUSSIAN_BLUR:
      case TOOL_PREP_KEYS.PIXEL_BLUR:
      case TOOL_PREP_KEYS.MOTION_BLUR:
      case TOOL_PREP_KEYS.BOKEH_BLUR:
      case TOOL_PREP_KEYS.BACKGROUND_BLUR:
        void deliverToolPrepResult(handleBackgroundColorToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.AI_EXPAND:
        void deliverToolPrepResult(handleOutpaintToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.AI_RECOLOR:
        void deliverToolPrepResult(handleRecolorToolPrepDone, result);
        break;
      case TOOL_PREP_KEYS.HEIC_TO_JPG:
      case TOOL_PREP_KEYS.WEBP_TO_JPG:
      case TOOL_PREP_KEYS.PNG_TO_JPG:
      case TOOL_PREP_KEYS.JPG_TO_PNG:
      case TOOL_PREP_KEYS.WEBP_TO_PNG:
        void deliverToolPrepResult(handleAiFeatureToolPrepDone, result);
        break;
      default:
        void deliverToolPrepResult(handleAiFeatureToolPrepDone, result);
        break;
    }
  }, [
    deliverToolPrepResult,
    handleAiFeatureToolPrepDone,
    handleBackgroundColorToolPrepDone,
    handleOutpaintToolPrepDone,
    handleOutlineToolPrepDone,
    handlePaddingToolPrepDone,
    handleCropToolPrepDone,
    handleRecolorToolPrepDone,
    handleShadowToolPrepDone,
    handleToolPrepDone,
    handleTransformToolPrepDone,
    handleWatermarkToolPrepDone,
    location,
    navigate,
  ]);


  // BG Remove handoff: show the subject box once the canvas image is ready.
  useEffect(() => {
    if (!pendingSelectObjectRef.current || !subjectReady) return;
    if (!canvasEditorRef.current?.isSubjectReady?.()) return;
    pendingSelectObjectRef.current = false;
    canvasEditorRef.current.selectObject?.();
  }, [subjectReady]);

  // Apply Editing Tools → Resize prep FIT/FILL after Done
  useEffect(() => {
    const pending = pendingToolPrepApplyRef.current;
    if (
      !pending ||
      !processedImage ||
      !subjectReady ||
      appliedToolPrepTransformRef.current
    ) {
      return;
    }
    if (!canvasEditorRef.current?.isSubjectReady?.()) {
      return;
    }
    applyToolPrepTransform(pending);
  }, [processedImage, subjectReady, applyToolPrepTransform]);

  // Apply Editing Tools → Padding prep after canvas subject is ready
  useEffect(() => {
    const pending = pendingPaddingApplyRef.current;
    if (
      !pending ||
      !processedImage ||
      !subjectReady ||
      appliedPaddingRef.current
    ) {
      return;
    }
    if (!canvasEditorRef.current?.isSubjectReady?.()) {
      return;
    }
    applyPaddingLayout(pending);
  }, [processedImage, subjectReady, applyPaddingLayout]);


  return {
    openToolPrepFromEditor,
    toolPrepNode: (
      <>
      {toolPrepKind === TOOL_PREP_KEYS.RESIZE ? (
        <ResizeToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          removeBgRequired={false}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleToolPrepDone, result)}
          onCancel={handleToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.SHADOW ? (
        <ShadowToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleShadowToolPrepDone, result)}
          onCancel={handleShadowToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.OUTLINE ? (
        <OutlineToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleOutlineToolPrepDone, result)}
          onCancel={handleOutlineToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.BACKGROUND_COLOR ? (
        <BackgroundColorToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) =>
            deliverToolPrepResult(handleBackgroundColorToolPrepDone, result)
          }
          onCancel={handleBackgroundColorToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.WHITE_BACKGROUND ? (
        <WhiteBackgroundToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) =>
            deliverToolPrepResult(handleBackgroundColorToolPrepDone, result)
          }
          onCancel={handleBackgroundColorToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.BLACK_BACKGROUND ? (
        <BlackBackgroundToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) =>
            deliverToolPrepResult(handleBackgroundColorToolPrepDone, result)
          }
          onCancel={handleBackgroundColorToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.ROTATE ? (
        <RotateToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleTransformToolPrepDone, result)}
          onCancel={handleTransformToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.FLIP ? (
        <FlipToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleTransformToolPrepDone, result)}
          onCancel={handleTransformToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.PADDING ? (
        <PaddingToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handlePaddingToolPrepDone, result)}
          onCancel={handleToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.CROP ? (
        <CropToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleCropToolPrepDone, result)}
          onCancel={handleToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.CIRCLE_CROP ? (
        <CircleCropToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleCropToolPrepDone, result)}
          onCancel={handleToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.WATERMARK_MAKER ? (
        <WatermarkToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) => deliverToolPrepResult(handleWatermarkToolPrepDone, result)}
          onCancel={handleWatermarkToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.BLUR ||
      toolPrepKind === TOOL_PREP_KEYS.GAUSSIAN_BLUR ||
      toolPrepKind === TOOL_PREP_KEYS.PIXEL_BLUR ||
      toolPrepKind === TOOL_PREP_KEYS.MOTION_BLUR ||
      toolPrepKind === TOOL_PREP_KEYS.BOKEH_BLUR ||
      toolPrepKind === TOOL_PREP_KEYS.BACKGROUND_BLUR ? (
        <BlurToolPrep
          toolKey={toolPrepKind}
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            setBgRemovedImageUrl(url);
            setRemoveBackgroundActive(true);
          }}
          onDone={(result) =>
            deliverToolPrepResult(handleBackgroundColorToolPrepDone, result)
          }
          onCancel={handleToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.PDF_WATERMARKER ? (
        <PdfWatermarkerToolPrep
          onCancel={handlePdfWatermarkerToolPrepCancel}
          onDone={(result) => deliverToolPrepResult(handleDocumentExportStayOpen, result)}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.WORD_WATERMARKER ? (
        <WordWatermarkerToolPrep
          onCancel={handleWordWatermarkerToolPrepCancel}
          onDone={(result) => deliverToolPrepResult(handleDocumentExportStayOpen, result)}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.PDF_PAGE_NUMBERS ? (
        <PdfPageNumbersToolPrep
          onCancel={handlePdfPageNumbersToolPrepCancel}
          onDone={(result) =>
            deliverToolPrepResult(handlePdfPageNumbersToolPrepDone, result)
          }
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.SIGN_PDF ? (
        <SignPdfToolPrep
          onCancel={handleToolPrepCancel}
          onDone={(result) => deliverToolPrepResult(handleDocumentExportStayOpen, result)}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_EXPAND ? (
        <OutpaintToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleOutpaintToolPrepDone, result)}
          onCancel={handleOutpaintToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_RECOLOR ? (
        <RecolorToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleRecolorToolPrepDone, result)}
          onCancel={handleRecolorToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_ENHANCE ? (
        <EnhanceToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleEnhanceToolPrepDone, result)}
          onCancel={handleEnhanceToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_BACKGROUND_IMAGE ? (
        <BackgroundImageToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            if (url) setBgRemovedImageUrl(url);
          }}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_SHADOW ? (
        <AiShadowToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          removeBgDefault={removeBackgroundActive}
          cachedCutoutUrl={bgRemovedImageUrl}
          onCutoutReady={(url) => {
            if (url) setBgRemovedImageUrl(url);
          }}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_REMOVE_OBJECT ? (
        <RemoveObjectToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_REMOVE_TEXT ? (
        <RemoveTextToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_UPSCALE ? (
        <UpscaleToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_RELIGHT ? (
        <RelightToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_REMOVE_WATERMARK ? (
        <RemoveWatermarkToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_BACKGROUND_REMOVER ? (
        <BackgroundRemoverToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_SHADOW_REMOVER ? (
        <ShadowRemoverToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_OLD_PHOTO_RESTORE ? (
        <OldPhotoRestoreToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_COLORIZER ? (
        <ColorizerToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_UNBLUR ? (
        <UnblurToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_REPLACE_OBJECT ? (
        <ReplaceObjectToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_REPLACE_TEXT ? (
        <ReplaceTextToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}

      {toolPrepKind === TOOL_PREP_KEYS.AI_FACE_SWAP ? (
        <FaceSwapToolPrep
          originalImageUrl={
            originalImageUrl || inputPreview || originalImg || null
          }
          imageFile={selectedFile}
          onDone={(result) => deliverToolPrepResult(handleAiFeatureToolPrepDone, result)}
          onCancel={handleAiFeatureToolPrepCancel}
        />
      ) : null}
      </>
    ),
  };
}
